import { Component, OnInit } from '@angular/core';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { AutoCompleteCompleteEvent, AutoCompleteModule } from 'primeng/autocomplete';
import { DialogModule } from 'primeng/dialog';
import { FileUploadModule } from 'primeng/fileupload';
import { CommonModule } from '@angular/common';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { FileService } from '../../core/services/file';
import { SubscriberService } from '../../core/services/subscriber.service';
import { EncryptionService } from '../../core/services/encryption';
import { LanguageService } from '../../core/services/language.service';
import { SETTINGS } from '../../core/config/common.settings';
import { firstValueFrom, Observable } from 'rxjs';
import { CanComponentDeactivate } from '../../core/guards/unsaved-changes.guard';

interface Country {
  code: string;
  name: string;
}

@Component({
  selector: 'app-company-config',
  imports: [
    FormsModule,
    InputTextModule,
    ButtonModule,
    AutoCompleteModule,
    DialogModule,
    FileUploadModule,
    CommonModule,
    ToastModule,
    SelectModule,
    TooltipModule,
    ConfirmDialogModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './company-config.html',
  styleUrl: './company-config.scss'
})
export class CompanyConfig implements OnInit, CanComponentDeactivate {
  value: string = '';
  items: any[] | undefined;
  value2: any;
  selectedContractDuration: any = null;
  selectedLanguage: any = null;
  currentSubscriber: any = null;
  isLoading: boolean = false;
  isDataLoading: boolean = true;

  // Data for dropdowns - using regions from common settings
  countries: Country[] = SETTINGS.REGIONS.map(region => ({
    code: region.code,
    name: region.name
  }));

  // Contract duration options
  contractDurations = [
    { label: '6 months', value: 6 },
    { label: '12 months', value: 12 }
  ];

  // Language options - based on available locale files
  languages = [
    { label: 'English', value: 'en' },
    { label: 'Deutsch (German)', value: 'de' },
    { label: 'Română (Romanian)', value: 'ro' }
  ];

  // Image upload popup properties
  showImageUploadDialog: boolean = false;
  selectedFile: File | null = null;
  imagePreview: string | null = null;
  isUploading: boolean = false;
  isGeneratingPreview: boolean = false;
  companyLogoUrl: string = '';
  dragOver: boolean = false;

  // Legal documents properties
  showLegalDocDialog: boolean = false;
  selectedLegalFile: File | null = null;
  legalDocType: 'terms' | 'privacy' | null = null;
  isLegalDocUploading: boolean = false;
  termsConditionsUrl: string = '';
  privacyPolicyUrl: string = '';

  // Company properties
  companyName: string = '';

  // Industries and regions properties
  newIndustry: string = '';
  industries: string[] = ['Finance', 'Health'];
  operationalRegions: string[] = ['United States', 'United Kingdom'];
  selectedRegion: Country | null = null;

  // Contract duration management properties
  newContractDuration: number | null = null;
  contractDurationsList: string[] = [];

  // Reservation duration management properties
  newReservationDuration: number | null = null;
  reservationDurationsList: string[] = [];

  // Original state for tracking unsaved changes
  private originalState: any = null;

  constructor(
    private fileService: FileService,
    private messageService: MessageService,
    private subscriberService: SubscriberService,
    private encryption: EncryptionService,
    private confirmationService: ConfirmationService,
    private languageService: LanguageService
  ) {}

  ngOnInit() {
    this.loadCurrentSubscriber();
  }

  // Method to manually refresh subscriber data
  refreshSubscriberData(): void {
    this.loadCurrentSubscriber();
  }

  loadCurrentSubscriber(): void {
    this.isDataLoading = true;
    const encryptedUserStr = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
    
    if (encryptedUserStr) {
      const userStr = this.encryption.decrypt(encryptedUserStr);
      const user = JSON.parse(userStr);
      
      const subscriberId = user.subscriberId?._id;
      
      if (subscriberId) {
        this.subscriberService.findSubscriber(subscriberId).then((subscriber: any) => {
          // Check if subscriber is wrapped in a response object
          let actualSubscriber = subscriber;
          if (subscriber && subscriber.subscriber) {
            actualSubscriber = subscriber.subscriber;
          }
          
          this.currentSubscriber = actualSubscriber;
          
          // Add a small delay to ensure form fields are ready
          setTimeout(() => {
            this.loadSubscriberData();
            this.isDataLoading = false;
          }, 100);
        }).catch((error: any) => {
          console.error('Error loading subscriber:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load subscriber data'
          });
          this.isDataLoading = false;
        });
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No subscriber ID found in user data'
        });
        this.isDataLoading = false;
      }
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No user data found'
      });
      this.isDataLoading = false;
    }
  }

  loadSubscriberData(): void {
    if (this.currentSubscriber) {
      // Load other fields first
      this.companyName = this.currentSubscriber.companyName || '';
      this.industries = this.currentSubscriber.industries || [];
      this.operationalRegions = this.currentSubscriber.operationalRegions || [];
      this.contractDurationsList = this.currentSubscriber.contractDurationsList || [];
      this.reservationDurationsList = this.currentSubscriber.reservationDurationsList || [];
      this.sortContractDurations();
      this.sortReservationDurations();
      
      // Load contract duration (reservation duration)
      if (this.currentSubscriber.contactDuration) {
        const durationValue = parseInt(this.currentSubscriber.contactDuration);
        // Check if the parsed value matches one of our reservation duration options
        const matchingDuration = this.reservationDurationsList.find(d => {
          const months = parseInt(d, 10);
          return months === durationValue;
        });
        if (matchingDuration) {
          this.selectedContractDuration = durationValue;
        } else {
          this.selectedContractDuration = null;
        }
      } else {
        this.selectedContractDuration = null;
      }
      
      // Load language
      if (this.currentSubscriber.language) {
        // Check if the language value matches one of our dropdown options
        if (this.languages.some(l => l.value === this.currentSubscriber.language)) {
          this.selectedLanguage = this.currentSubscriber.language;
        } else {
          this.selectedLanguage = null;
        }
      } else {
        this.selectedLanguage = null;
      }
      
      // Load file URLs
      this.companyLogoUrl = this.currentSubscriber.companyLogo || '';
      this.termsConditionsUrl = this.currentSubscriber.termsConditions || '';
      this.privacyPolicyUrl = this.currentSubscriber.privacyPolicy || '';
      
      // Store original state for unsaved changes tracking
      this.saveOriginalState();
      
      console.log('File URLs loaded:', {
        companyLogo: this.companyLogoUrl,
        termsConditions: this.termsConditionsUrl,
        privacyPolicy: this.privacyPolicyUrl
      });
    }
  }

  saveAllSettings(): void {
    if (!this.currentSubscriber) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No subscriber data available'
      });
      return;
    }

    // Get subscriber ID from current subscriber or fallback to user data
    let subscriberId = this.currentSubscriber._id;
    if (!subscriberId) {
      // Fallback: try to get from user data
      const encryptedUserStr = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
      if (encryptedUserStr) {
        const userStr = this.encryption.decrypt(encryptedUserStr);
        const user = JSON.parse(userStr);
        subscriberId = user.subscriberId?._id;
      }
    }

    if (!subscriberId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No subscriber ID available for update'
      });
      return;
    }

    // Validate language if selected
    if (this.selectedLanguage && !this.languageService.isValidLanguage(this.selectedLanguage)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Invalid language selection. Please select a valid language.'
      });
      return;
    }

    this.isLoading = true;
    
    const updatePayload = {
      _id: subscriberId,
      companyName: this.companyName,
      industries: this.industries,
      operationalRegions: this.operationalRegions,
      contactDuration: this.selectedContractDuration ? this.selectedContractDuration.toString() : null,
      contractDurationsList: this.contractDurationsList,
      reservationDurationsList: this.reservationDurationsList,
      language: this.selectedLanguage || null,
      companyLogo: this.companyLogoUrl || null,
      termsConditions: this.termsConditionsUrl || null,
      privacyPolicy: this.privacyPolicyUrl || null
    };

    console.log('Saving subscriber settings:', {
      language: this.selectedLanguage,
      companyLogo: this.companyLogoUrl,
      termsConditions: this.termsConditionsUrl,
      privacyPolicy: this.privacyPolicyUrl
    });
    console.log('Full update payload:', updatePayload);

    this.subscriberService.updateSubscriber(updatePayload).then((updatedSubscriber: any) => {
      this.currentSubscriber = updatedSubscriber;
      
      // Update original state after successful save
      this.saveOriginalState();
      
      // Show success message with language info if changed
      let successMessage = 'Settings saved successfully';
      if (this.selectedLanguage) {
        const langName = this.languages.find(l => l.value === this.selectedLanguage)?.label || this.selectedLanguage;
        successMessage = `Settings saved successfully. Default language set to ${langName}. All users in this subscription will see this language after completing two-factor authentication on their next login.`;
        console.log('Subscriber language updated:', {
          subscriberId: subscriberId,
          language: this.selectedLanguage,
          languageName: langName
        });
      }
      
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: successMessage,
        life: 5000
      });
      this.isLoading = false;
    }).catch((error: any) => {
      console.error('Error updating subscriber:', error);
      let errorMessage = 'Failed to save settings';
      if (error.error?.message) {
        errorMessage = error.error.message;
      }
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage
      });
      this.isLoading = false;
    });
  }

  search(event: AutoCompleteCompleteEvent) {
    let _items = [...Array(10).keys()];
    this.items = event.query ? [...Array(10).keys()].map((item) => event.query + '-' + item) : _items;
  }

  // Check if there's an existing logo
  hasExistingLogo(): boolean {
    return !!this.companyLogoUrl && this.companyLogoUrl.trim() !== '';
  }

  // Check if legal documents exist
  hasTermsConditions(): boolean {
    return !!this.termsConditionsUrl && this.termsConditionsUrl.trim() !== '';
  }

  hasPrivacyPolicy(): boolean {
    return !!this.privacyPolicyUrl && this.privacyPolicyUrl.trim() !== '';
  }

  // Open image upload dialog
  openImageUploadDialog() {
    this.showImageUploadDialog = true;
    this.selectedFile = null;
    this.imagePreview = null;
    this.dragOver = false;
    
    // Show different message based on whether there's an existing logo
    if (this.hasExistingLogo()) {
      this.messageService.add({
        severity: 'info',
        summary: 'Replace logo',
        detail: 'You can upload a new image to replace the current logo'
      });
    }
  }

  // Handle file selection
  onFileSelect(event: any) {
    const file = event.files?.[0];
    if (file) {
      // Clear previous file and preview
      this.clearSelectedFile();
      
      // Validate file
      if (!this.validateFile(file)) {
        return;
      }
      
      this.selectedFile = file;
      this.generateImagePreview(file);
      
      // Show success message
      this.messageService.add({
        severity: 'success',
        summary: 'File selected',
        detail: `${file.name} has been selected successfully`
      });
    }
  }

  // Handle file upload from input change
  onFileUpload(event: any) {
    const file = event.target.files?.[0];
    if (file) {
      this.onFileSelect({ files: [file] });
    }
  }

  // Drag and drop methods
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    
    // Handle based on which dialog is open
    if (this.showLegalDocDialog) {
      this.onLegalDocFileSelect({ files: [file] });
    } else if (this.showImageUploadDialog) {
      this.onFileSelect({ files: [file] });
    }
  }

  // Trigger file input
  triggerFileInput(): void {
    if (this.isUploading || this.isLegalDocUploading) return;
    
    let fileInput: HTMLInputElement | null = null;
    
    if (this.showLegalDocDialog) {
      fileInput = document.getElementById('file-input-legal-doc') as HTMLInputElement;
    } else if (this.showImageUploadDialog) {
      fileInput = document.getElementById('file-input-logo') as HTMLInputElement;
    }
    
    if (fileInput) {
      fileInput.click();
    }
  }

  // Validate file type and size
  private validateFile(file: File): boolean {
    // Check if file exists
    if (!file) {
      this.messageService.add({
        severity: 'error',
        summary: 'No file',
        detail: 'Please select a file to upload'
      });
      return false;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid file type',
        detail: 'Please select an image file (PNG, JPG, JPEG)'
      });
      return false;
    }

    // Check file size (1MB = 1,048,576 bytes)
    const maxSize = 1048576;
    if (file.size > maxSize) {
      this.messageService.add({
        severity: 'error',
        summary: 'File too large',
        detail: `File size must be less than 1MB. Current size: ${this.formatFileSize(file.size)}`
      });
      return false;
    }

    // Check if file is empty
    if (file.size === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Empty file',
        detail: 'The selected file is empty'
      });
      return false;
    }

    return true;
  }

  // Format file size for display
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Generate image preview
  private generateImagePreview(file: File) {
    this.isGeneratingPreview = true;
    
    try {
      if (file.type.startsWith('image/')) {
        this.imagePreview = URL.createObjectURL(file);
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Preview error',
        detail: 'Failed to generate image preview'
      });
    } finally {
      this.isGeneratingPreview = false;
    }
  }

  // Upload image
  async uploadImage() {
    if (!this.selectedFile) {
      this.messageService.add({
        severity: 'error',
        summary: 'No file selected',
        detail: 'Please select an image file to upload'
      });
      return;
    }

    // Double-check file validation
    if (!this.validateFile(this.selectedFile)) {
      return;
    }

    this.isUploading = true;

    try {
      const response = await firstValueFrom(this.fileService.postFile(this.selectedFile, 'COMPANY_LOGO_UPLOAD'));
      
      // Validate response
      if (!response || !response.url) {
        throw new Error('Invalid response from server');
      }
      
      // Check if logo has changed
      if (this.hasLogoChanged(response.url)) {
        this.companyLogoUrl = response.url;
        
        // Automatically save the logo URL to the database
        await this.saveLogoToDatabase(response.url);
        
        this.messageService.add({
          severity: 'success',
          summary: 'Upload successful',
          detail: 'Company logo has been updated and saved successfully'
        });
      } else {
        this.messageService.add({
          severity: 'info',
          summary: 'No changes',
          detail: 'Logo is the same as the current one'
        });
      }

      // Close dialog and update preview
      this.showImageUploadDialog = false;
      this.resetUploadState();

    } catch (error: any) {
      console.error('Error uploading image:', error);
      
      let errorMessage = 'Failed to upload image';
      if (error.error?.message) {
        errorMessage = error.error.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      this.messageService.add({
        severity: 'error',
        summary: 'Upload failed',
        detail: errorMessage
      });
    } finally {
      this.isUploading = false;
    }
  }

  // Check if logo has changed
  private hasLogoChanged(newLogoUrl: string): boolean {
    return this.companyLogoUrl !== newLogoUrl;
  }

  // Save logo URL to database
  private async saveLogoToDatabase(logoUrl: string): Promise<void> {
    if (!this.currentSubscriber) {
      throw new Error('No subscriber data available');
    }

    // Get subscriber ID from current subscriber or fallback to user data
    let subscriberId = this.currentSubscriber._id;
    if (!subscriberId) {
      // Fallback: try to get from user data
      const encryptedUserStr = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
      if (encryptedUserStr) {
        const userStr = this.encryption.decrypt(encryptedUserStr);
        const user = JSON.parse(userStr);
        subscriberId = user.subscriberId?._id;
      }
    }

    if (!subscriberId) {
      throw new Error('No subscriber ID available for update');
    }

    const updatePayload = {
      _id: subscriberId,
      companyLogo: logoUrl
    };

    try {
      const updatedSubscriber = await this.subscriberService.updateSubscriber(updatePayload);
      this.currentSubscriber = updatedSubscriber;
      // Update original state after successful save
      this.saveOriginalState();
      console.log('Logo URL saved to database:', logoUrl);
    } catch (error: any) {
      console.error('Error saving logo URL:', error);
      throw new Error('Failed to save logo URL to database');
    }
  }

  // Save legal document URL to database
  private async saveLegalDocToDatabase(): Promise<void> {
    if (!this.currentSubscriber) {
      throw new Error('No subscriber data available');
    }

    // Get subscriber ID from current subscriber or fallback to user data
    let subscriberId = this.currentSubscriber._id;
    if (!subscriberId) {
      // Fallback: try to get from user data
      const encryptedUserStr = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
      if (encryptedUserStr) {
        const userStr = this.encryption.decrypt(encryptedUserStr);
        const user = JSON.parse(userStr);
        subscriberId = user.subscriberId?._id;
      }
    }

    if (!subscriberId) {
      throw new Error('No subscriber ID available for update');
    }

    const updatePayload = {
      _id: subscriberId,
      termsConditions: this.termsConditionsUrl,
      privacyPolicy: this.privacyPolicyUrl
    };

    try {
      const updatedSubscriber = await this.subscriberService.updateSubscriber(updatePayload);
      this.currentSubscriber = updatedSubscriber;
      // Update original state after successful save
      this.saveOriginalState();
      console.log('Legal document URLs saved to database:', {
        termsConditions: this.termsConditionsUrl,
        privacyPolicy: this.privacyPolicyUrl
      });
    } catch (error: any) {
      console.error('Error saving legal document URLs:', error);
      throw new Error('Failed to save legal document URLs to database');
    }
  }

  // Cancel upload
  cancelUpload() {
    if (this.isUploading) {
      // If currently uploading, show a message
      this.messageService.add({
        severity: 'warn',
        summary: 'Upload in progress',
        detail: 'Please wait for the upload to complete or try again'
      });
      return;
    }
    
    // If there's a selected file, ask for confirmation
    if (this.selectedFile) {
      if (confirm('Are you sure you want to cancel? Any selected file will be lost.')) {
        this.closeImageUploadDialog();
      }
    } else {
      this.closeImageUploadDialog();
    }
  }

  // Reset upload state
  private resetUploadState() {
    this.clearSelectedFile();
    this.isUploading = false;
  }

  // Close dialog
  closeImageUploadDialog() {
    this.showImageUploadDialog = false;
    this.resetUploadState();
  }

  // Remove selected file
  removeSelectedFile() {
    this.selectedFile = null;
    this.imagePreview = null;
  }

  // Clear selected file and preview
  private clearSelectedFile() {
    if (this.imagePreview) {
      URL.revokeObjectURL(this.imagePreview);
    }
    this.selectedFile = null;
    this.imagePreview = null;
    this.isGeneratingPreview = false;
  }

  // Remove existing company logo
  removeCompanyLogo() {
    if (this.hasExistingLogo()) {
      if (confirm('Are you sure you want to remove the company logo? This action cannot be undone.')) {
        this.companyLogoUrl = '';
        this.messageService.add({
          severity: 'info',
          summary: 'Logo removed',
          detail: 'Company logo has been removed successfully'
        });
      }
    } else {
      this.messageService.add({
        severity: 'info',
        summary: 'No logo',
        detail: 'There is no logo to remove'
      });
    }
  }

  // View logo in larger size
  viewLogo() {
    if (this.hasExistingLogo()) {
      try {
        // Create a new window with the logo
        const newWindow = window.open(this.companyLogoUrl, '_blank');
        if (!newWindow) {
          // If popup is blocked, show a message
          this.messageService.add({
            severity: 'warn',
            summary: 'Popup blocked',
            detail: 'Please allow popups to view the logo in a new tab'
          });
        }
      } catch (error) {
        console.error('Error opening logo:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to open logo in new tab'
        });
      }
    } else {
      this.messageService.add({
        severity: 'info',
        summary: 'No logo',
        detail: 'There is no logo to view'
      });
    }
  }

  // Industries and Regions Methods
  addIndustry() {
    if (this.newIndustry.trim()) {
      if (!this.industries.includes(this.newIndustry.trim())) {
        this.industries.push(this.newIndustry.trim());
        this.newIndustry = '';
        this.messageService.add({
          severity: 'success',
          summary: 'Industry added',
          detail: 'Industry has been added successfully'
        });
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: 'Industry exists',
          detail: 'This industry already exists'
        });
      }
    }
  }

  removeIndustry(industry: string) {
    const index = this.industries.indexOf(industry);
    if (index > -1) {
      this.industries.splice(index, 1);
      this.messageService.add({
        severity: 'info',
        summary: 'Industry removed',
        detail: 'Industry has been removed successfully'
      });
    }
  }

  // Handle Enter key press for industries
  onIndustryKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addIndustry();
    }
  }

  // Handle region selection from dropdown
  onRegionSelect(event: any) {
    console.log('onRegionSelect event:', event);
    if (event.value) {
      const selectedCountry = event.value as Country;
      console.log('Selected country:', selectedCountry);
      if (!this.operationalRegions.includes(selectedCountry.name)) {
        this.operationalRegions.push(selectedCountry.name);
        this.selectedRegion = null; // Reset selection
        this.messageService.add({
          severity: 'success',
          summary: 'Region added',
          detail: `${selectedCountry.name} has been added as an operational region`
        });
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: 'Region exists',
          detail: 'This operational region already exists'
        });
        this.selectedRegion = null; // Reset selection
      }
    }
  }

  // Handle Add button click for regions
  addSelectedRegion() {
    console.log('addSelectedRegion called, selectedRegion:', this.selectedRegion);
    if (this.selectedRegion) {
      const selectedCountry = this.selectedRegion as Country;
      console.log('Selected country from button:', selectedCountry);
      if (!this.operationalRegions.includes(selectedCountry.name)) {
        this.operationalRegions.push(selectedCountry.name);
        this.selectedRegion = null; // Reset selection
        this.messageService.add({
          severity: 'success',
          summary: 'Region added',
          detail: `${selectedCountry.name} has been added as an operational region`
        });
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: 'Region exists',
          detail: 'This operational region already exists'
        });
        this.selectedRegion = null; // Reset selection
      }
    }
  }

  // Handle region selection change
  onRegionChange(event: any) {
    this.selectedRegion = event.value;
  }

  // Remove region from the list
  removeRegion(region: string) {
    const index = this.operationalRegions.indexOf(region);
    if (index > -1) {
      this.operationalRegions.splice(index, 1);
      this.messageService.add({
        severity: 'info',
        summary: 'Region removed',
        detail: 'Operational region has been removed successfully'
      });
    }
  }

  // Contract Duration Management Methods
  addContractDuration() {
    if (this.newContractDuration !== null && this.newContractDuration > 0) {
      const numericValue = this.newContractDuration;
      
      // Format as "X months"
      const formattedDuration = `${numericValue} months`;
      
      if (!this.contractDurationsList.includes(formattedDuration)) {
        this.contractDurationsList.push(formattedDuration);
        this.sortContractDurations();
        this.newContractDuration = null;
        this.messageService.add({
          severity: 'success',
          summary: 'Duration added',
          detail: `${formattedDuration} has been added successfully`
        });
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: 'Duration exists',
          detail: 'This contract duration already exists'
        });
      }
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid input',
        detail: 'Please enter a valid positive number for months'
      });
    }
  }

  removeContractDuration(duration: string) {
    const index = this.contractDurationsList.indexOf(duration);
    if (index > -1) {
      this.contractDurationsList.splice(index, 1);
      this.sortContractDurations();
      this.messageService.add({
        severity: 'info',
        summary: 'Duration removed',
        detail: 'Contract duration has been removed successfully'
      });
    }
  }

  private sortContractDurations(): void {
    this.contractDurationsList.sort((a: string, b: string) => {
      const getMonths = (s: string) => parseInt(s, 10) || 0;
      return getMonths(a) - getMonths(b);
    });
  }

  // Handle Enter key press for contract durations
  onContractDurationKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addContractDuration();
    }
  }

  // Reservation Duration Management Methods
  addReservationDuration() {
    if (this.newReservationDuration !== null && this.newReservationDuration > 0) {
      const numericValue = this.newReservationDuration;
      
      // Format as "X months"
      const formattedDuration = `${numericValue} months`;
      
      if (!this.reservationDurationsList.includes(formattedDuration)) {
        this.reservationDurationsList.push(formattedDuration);
        this.sortReservationDurations();
        this.newReservationDuration = null;
        this.messageService.add({
          severity: 'success',
          summary: 'Duration added',
          detail: `${formattedDuration} has been added successfully`
        });
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: 'Duration exists',
          detail: 'This reservation duration already exists'
        });
      }
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid input',
        detail: 'Please enter a valid positive number for months'
      });
    }
  }

  removeReservationDuration(duration: string) {
    const index = this.reservationDurationsList.indexOf(duration);
    if (index > -1) {
      this.reservationDurationsList.splice(index, 1);
      this.sortReservationDurations();
      this.messageService.add({
        severity: 'info',
        summary: 'Duration removed',
        detail: 'Reservation duration has been removed successfully'
      });

      // If the removed duration was the selected one, clear the selection
      if (this.selectedContractDuration === duration || 
          (typeof this.selectedContractDuration === 'number' && this.selectedContractDuration.toString() + ' months' === duration)) {
        this.selectedContractDuration = null;
      }
    }
  }

  private sortReservationDurations(): void {
    this.reservationDurationsList.sort((a: string, b: string) => {
      const getMonths = (s: string) => parseInt(s, 10) || 0;
      return getMonths(a) - getMonths(b);
    });
  }

  // Handle Enter key press for reservation durations
  onReservationDurationKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addReservationDuration();
    }
  }

  // Convert reservation durations list to options format for dropdown
  getReservationDurationOptions(): any[] {
    return this.reservationDurationsList.map(duration => {
      const months = parseInt(duration, 10);
      return {
        label: duration,
        value: months
      };
    });
  }

  // Legal Document Methods
  openLegalDocDialog(type: 'terms' | 'privacy') {
    this.legalDocType = type;
    this.showLegalDocDialog = true;
    this.selectedLegalFile = null;
    this.dragOver = false;
    
    const docName = type === 'terms' ? 'Terms & Conditions' : 'Privacy Policy';
    const existingUrl = type === 'terms' ? this.termsConditionsUrl : this.privacyPolicyUrl;
    
    if (existingUrl) {
      this.messageService.add({
        severity: 'info',
        summary: 'Replace document',
        detail: `You can upload a new ${docName} document to replace the current one`
      });
    }
  }

  // Handle legal document file selection
  onLegalDocFileSelect(event: any) {
    const file = event.files?.[0];
    if (file) {
      // Clear previous file
      this.clearSelectedLegalFile();
      
      // Validate file
      if (!this.validateLegalDocFile(file)) {
        return;
      }
      
      this.selectedLegalFile = file;
      
      // Show success message
      this.messageService.add({
        severity: 'success',
        summary: 'File selected',
        detail: `${file.name} has been selected successfully`
      });
    }
  }

  // Handle legal document file upload from input change
  onLegalDocFileUpload(event: any) {
    const file = event.target.files?.[0];
    if (file) {
      this.onLegalDocFileSelect({ files: [file] });
    }
  }

  // Validate legal document file
  private validateLegalDocFile(file: File): boolean {
    // Check if file exists
    if (!file) {
      this.messageService.add({
        severity: 'error',
        summary: 'No file',
        detail: 'Please select a file to upload'
      });
      return false;
    }

    // Check file type (PDF only)
    if (file.type !== 'application/pdf') {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid file type',
        detail: 'Please select a PDF file'
      });
      return false;
    }

    // Check file size (5MB = 5,242,880 bytes)
    const maxSize = 5242880;
    if (file.size > maxSize) {
      this.messageService.add({
        severity: 'error',
        summary: 'File too large',
        detail: `File size must be less than 5MB. Current size: ${this.formatFileSize(file.size)}`
      });
      return false;
    }

    // Check if file is empty
    if (file.size === 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Empty file',
        detail: 'The selected file is empty'
      });
      return false;
    }

    return true;
  }

  // Upload legal document
  async uploadLegalDocument() {
    if (!this.selectedLegalFile || !this.legalDocType) {
      this.messageService.add({
        severity: 'error',
        summary: 'No file selected',
        detail: 'Please select a document file to upload'
      });
      return;
    }

    // Double-check file validation
    if (!this.validateLegalDocFile(this.selectedLegalFile)) {
      return;
    }

    this.isLegalDocUploading = true;

    try {
      const uploadType = this.legalDocType === 'terms' ? 'TERMS_CONDITIONS_UPLOAD' : 'PRIVACY_POLICY_UPLOAD';
      const response = await firstValueFrom(this.fileService.postFile(this.selectedLegalFile, uploadType));
      
      // Validate response
      if (!response || !response.url) {
        throw new Error('Invalid response from server');
      }
      
      // Update the appropriate URL
      if (this.legalDocType === 'terms') {
        this.termsConditionsUrl = response.url;
      } else {
        this.privacyPolicyUrl = response.url;
      }

      // Automatically save the document URL to the database
      await this.saveLegalDocToDatabase();

      const docName = this.legalDocType === 'terms' ? 'Terms & Conditions' : 'Privacy Policy';
      this.messageService.add({
        severity: 'success',
        summary: 'Upload successful',
        detail: `${docName} document has been uploaded and saved successfully`
      });

      // Close dialog and reset state
      this.showLegalDocDialog = false;
      this.resetLegalDocState();

    } catch (error: any) {
      console.error('Error uploading legal document:', error);
      
      let errorMessage = 'Failed to upload document';
      if (error.error?.message) {
        errorMessage = error.error.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      this.messageService.add({
        severity: 'error',
        summary: 'Upload failed',
        detail: errorMessage
      });
    } finally {
      this.isLegalDocUploading = false;
    }
  }

  // Clear selected legal document file
  private clearSelectedLegalFile() {
    this.selectedLegalFile = null;
  }

  // Reset legal document upload state
  private resetLegalDocState() {
    this.clearSelectedLegalFile();
    this.isLegalDocUploading = false;
    this.legalDocType = null;
  }

  // Close legal document dialog
  closeLegalDocDialog() {
    this.showLegalDocDialog = false;
    this.resetLegalDocState();
  }

  // Cancel legal document upload
  cancelLegalDocUpload() {
    if (this.isLegalDocUploading) {
      // If currently uploading, show a message
      this.messageService.add({
        severity: 'warn',
        summary: 'Upload in progress',
        detail: 'Please wait for the upload to complete or try again'
      });
      return;
    }
    
    // If there's a selected file, ask for confirmation
    if (this.selectedLegalFile) {
      if (confirm('Are you sure you want to cancel? Any selected file will be lost.')) {
        this.closeLegalDocDialog();
      }
    } else {
      this.closeLegalDocDialog();
    }
  }

  // View legal document
  viewLegalDocument(type: 'terms' | 'privacy') {
    const url = type === 'terms' ? this.termsConditionsUrl : this.privacyPolicyUrl;
    const docName = type === 'terms' ? 'Terms & Conditions' : 'Privacy Policy';
    
    if (url) {
      try {
        // Create a new window with the logo
        const newWindow = window.open(url, '_blank');
        if (!newWindow) {
          // If popup is blocked, show a message
          this.messageService.add({
            severity: 'warn',
            summary: 'Popup blocked',
            detail: 'Please allow popups to view the document in a new tab'
          });
        }
      } catch (error) {
        console.error('Error opening document:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to open document in new tab'
        });
      }
    } else {
      this.messageService.add({
        severity: 'info',
        summary: 'No document',
        detail: `There is no ${docName} document to view`
      });
    }
  }

  // Save original state for comparison
  private saveOriginalState(): void {
    this.originalState = {
      companyName: this.companyName,
      industries: [...this.industries],
      operationalRegions: [...this.operationalRegions],
      contractDurationsList: [...this.contractDurationsList],
      reservationDurationsList: [...this.reservationDurationsList],
      selectedContractDuration: this.selectedContractDuration,
      selectedLanguage: this.selectedLanguage,
      companyLogoUrl: this.companyLogoUrl,
      termsConditionsUrl: this.termsConditionsUrl,
      privacyPolicyUrl: this.privacyPolicyUrl
    };
  }

  // Check if there are unsaved changes
  private hasUnsavedChanges(): boolean {
    if (!this.originalState) {
      return false;
    }

    // Compare all fields
    if (this.companyName !== this.originalState.companyName) {
      return true;
    }

    if (this.selectedContractDuration !== this.originalState.selectedContractDuration) {
      return true;
    }

    if (this.selectedLanguage !== this.originalState.selectedLanguage) {
      return true;
    }

    if (this.companyLogoUrl !== this.originalState.companyLogoUrl) {
      return true;
    }

    if (this.termsConditionsUrl !== this.originalState.termsConditionsUrl) {
      return true;
    }

    if (this.privacyPolicyUrl !== this.originalState.privacyPolicyUrl) {
      return true;
    }

    // Compare arrays
    if (this.arraysEqual(this.industries, this.originalState.industries) === false) {
      return true;
    }

    if (this.arraysEqual(this.operationalRegions, this.originalState.operationalRegions) === false) {
      return true;
    }

    if (this.arraysEqual(this.contractDurationsList, this.originalState.contractDurationsList) === false) {
      return true;
    }

    if (this.arraysEqual(this.reservationDurationsList, this.originalState.reservationDurationsList) === false) {
      return true;
    }

    return false;
  }

  // Helper method to compare arrays
  private arraysEqual(arr1: string[], arr2: string[]): boolean {
    if (arr1.length !== arr2.length) {
      return false;
    }
    const sorted1 = [...arr1].sort();
    const sorted2 = [...arr2].sort();
    return sorted1.every((val, index) => val === sorted2[index]);
  }

  // CanDeactivate implementation
  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.hasUnsavedChanges()) {
      return new Promise<boolean>((resolve) => {
        this.confirmationService.confirm({
          message: 'You have unsaved changes. Are you sure you want to leave this page?',
          header: 'Unsaved Changes',
          icon: 'pi pi-exclamation-triangle',
          acceptButtonStyleClass: 'p-button-danger',
          accept: () => {
            resolve(true);
          },
          reject: () => {
            resolve(false);
          }
        });
      });
    }
    return true;
  }
}

